import { useMemo } from 'react'

import { HelpOutline } from '@mui/icons-material'
import { Box, Tooltip, Typography } from '@mui/material'

import { IGridColumn } from 'components/common'

const useGetExportContactsColumns = () => {
  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'exportable',
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip
              title={
                <Typography variant='body2'>
                  This field indicates whether a contact can be exported. A contact is considered
                  exportable only if the related <strong>Customer</strong> has a HubSpot record ID.
                </Typography>
              }
              arrow>
              <HelpOutline sx={{ fontSize: 16, cursor: 'help', mr: 0.5 }} />
            </Tooltip>
            <strong>Exportable</strong>
          </Box>
        ),
        minWidth: 150,
        valueGetter: (params: any) => (params.row.exportable ? 'Yes' : 'No'),
        cellClassName: (params: any) => {
          return params.row.exportable ? 'cell-exportable' : 'cell-not-exportable'
        },
      },
      {
        field: 'exportStatus',
        headerName: 'Export Status',
        minWidth: 150,
        cellClassName: (params: any) => {
          const status = params.row.exportStatus
          if (status === 'Not Synced') return 'cell-not-synced'
          if (status === 'Synced') return 'cell-synced'
          if (status === 'Failed') return 'cell-failed'
          return ''
        },
      },
      {
        field: 'id',
        headerName: 'Contact ID',
        minWidth: 120,
        sortPath: ['contact', 'id'],
        valueGetter: (params: any) => params.row.contact?.id || '--',
      },
      {
        field: 'hubSpotSyncDate',
        headerName: 'Timestamp of export attempt',
        minWidth: 250,
        sortPath: ['contact', 'hubSpotSyncDate'],
        valueGetter: (params: any) => params.row.contact?.hubSpotSyncDate,
        valueFormatter: (params: any) =>
          params.value ? new Date(params.value).toLocaleString('en-GB') : '--',
      },
      {
        field: 'hubSpotObjectId',
        headerName: 'HS Record ID',
        minWidth: 150,
        sortPath: ['contact', 'hubSpotObjectId'],
        valueGetter: (params: any) => params.row.contact?.hubSpotObjectId || '--',
      },
      {
        field: 'customerName',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Company Name</strong>
            <br />
            (Company)
          </Box>
        ),
        minWidth: 250,
        valueGetter: (params: any) => params.row.customerName || '--',
      },
      {
        field: 'email',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Email</strong>
            <br />
            (Email)
          </Box>
        ),
        minWidth: 250,
        sortPath: ['contact', 'email'],
        valueGetter: (params: any) => params.row.contact?.email || '--',
      },
      {
        field: 'name',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Contact Name</strong>
            <br />
            (First Name & Last Name)
          </Box>
        ),
        minWidth: 250,
        sortPath: ['contact', 'name'],
        valueGetter: (params: any) => params.row.contact?.name || '--',
      },
      {
        field: 'telephoneNumber',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Telephone Number</strong>
            <br />
            (Phone)
          </Box>
        ),
        minWidth: 200,
        sortPath: ['contact', 'telephoneNumber'],
        valueGetter: (params: any) => params.row.contact?.telephoneNumber || '--',
      },
      {
        field: 'City',
        minWidth: 200,
        sortPath: ['customerCity'],
        valueGetter: (params: any) => params.row.customerCity || '--',
      },
      {
        field: 'customerPostcode',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Postcode</strong>
            <br />
            (Zip)
          </Box>
        ),
        minWidth: 150,
        valueGetter: (params: any) => params.row.customerPostcode || '--',
      },
      {
        field: 'createdBy',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Created By</strong>
            <br />
            (Contact Owner)
          </Box>
        ),
        minWidth: 250,
        sortPath: ['contact', 'createdBy'],
        valueGetter: (params: any) => params.row.contact?.createdBy || '--',
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        minWidth: 200,
        sortPath: ['contact', 'createdAt'],
        valueGetter: (params: any) => params.row.contact?.createdAt,
        valueFormatter: (params: any) =>
          params.value ? new Date(params.value).toLocaleString('en-GB') : '--',
      },
    ],
    [],
  )

  return columns
}

export default useGetExportContactsColumns
